import { Col, Flex, Grid, Layout, Row } from 'antd';
import MobilePhoneIcon from 'assets/svg/mobile-phone.svg';
import PhoneIcon from 'assets/svg/phone.svg';
import Cookies from 'js-cookie';
import PartWrapper from 'mixins/ui/part-wrapper';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { localize } from '/react4xp/mixins/helpers';
import Breadcrumbs from '/react4xp/parts/breadcrumbs/breadcrumbs';
import ButtonAuth from '/react4xp/parts/button-auth/button-auth';
import ButtonVisuallyImpaired from '/react4xp/parts/button-visually-impaired/button-visually-impaired';
import ContactCenter from '/react4xp/parts/contact-center/contact-center';
import HeaderMenu from '/react4xp/parts/header/header-menu';
import HeaderMobileMenu from '/react4xp/parts/header/header-mobile-menu';
import LanguageSwitcher from '/react4xp/parts/language-switcher/language-switcher';
import UserDropdown from '/react4xp/parts/language-switcher/user-dropdown';
import LogoWithSiteName from '/react4xp/parts/logo-with-site-name/logo-with-site-name';
import { getCachedTranslations, getTranslations } from '/react4xp/parts/main/actions';
import { getMainBlocks } from '/react4xp/parts/main/main.const';

import './header.scss';

const { Header: AntdHeader } = Layout;
const { useBreakpoint } = Grid;

const Header: FC = (props: any) => {
    const [translations, setTranslations] = useState(() => {
        return getCachedTranslations(
            props.lang,
            'i18n/phrases,i18n/user-dropdown/phrases,i18n/block-auth/phrases,i18n/header/phrases'
        );
    });
    const [arr, setArr] = useState([]);
    const { md, lg } = useBreakpoint();

    useEffect(() => {
        if (props.redirect) {
            window.location.href = props.path;
        } else {
            if (props.access_token) {
                window.location.href = `/${props.language}/pages/login?register=true`;
            }
        }

        getTranslations(
            props.lang,
            'i18n/phrases,i18n/user-dropdown/phrases,i18n/block-auth/phrases,i18n/header/phrases'
        ).then((value) => {
            setTranslations(value.data);
            setArr(getMainBlocks(props, value.data));
        });
    }, []);

    useEffect(() => {
        if (!props.user) {
            Cookies.remove('token');
            Cookies.remove('refresh_token');
        }
    }, [props.user]);

    useEffect(() => {
        if (props.token && !Cookies.get('token')) {
            Cookies.set('token', props.token, { path: '/', expires: 600 / 86400 }); // 10 минут
        }
    }, [props.token]);

    const renderBlockComponents = (index: number): ReactNode[] => {
        return arr
            .filter((x) => props.config[`${x.id}Weight`] === index)
            .map((x) => {
                const isHidden = props.config[`${x.id}IsHidden`];

                return !isHidden && x.node;
            });
    };

    const goToKnp = () => {
        window.location.href = props.config.knpBaseUrl;
    };

    return (
        <>
            <AntdHeader className="header">
                <div className="container">
                    <Row>
                        <Col
                            className="show-on-tablet"
                            xs={4}
                        >
                            <Flex
                                justify="center"
                                align="center"
                            >
                                <HeaderMobileMenu
                                    {...props}
                                    translations={translations}
                                />
                            </Flex>
                        </Col>

                        <Col
                            className="hide-on-tablet hide-on-mobile"
                            lg={12}
                        >
                            <HeaderMenu {...props} />
                        </Col>

                        <Col
                            xs={20}
                            lg={12}
                        >
                            <Flex
                                justify={'end'}
                                align={'center'}
                                gap={10}
                                style={{
                                    height: '100%',
                                }}
                            >
                                <ButtonVisuallyImpaired
                                    {...props}
                                    translations={translations}
                                />

                                <LanguageSwitcher
                                    {...props}
                                    translations={translations}
                                />

                                {props.user && (
                                    <UserDropdown
                                        {...props}
                                        translations={translations}
                                    />
                                )}

                                {props.token && (
                                    <a
                                        href="#"
                                        onClick={goToKnp}
                                        style={{ color: 'white' }}
                                    >
                                        {localize('go-to-knp', translations)}
                                    </a>
                                )}
                            </Flex>
                        </Col>
                    </Row>
                </div>
            </AntdHeader>

            <div className="logo-region">
                <div className="container">
                    <Row
                        style={{ padding: '20px 0' }}
                        gutter={[0, 15]}
                    >
                        <Col
                            xs={24}
                            md={13}
                        >
                            <LogoWithSiteName {...props} />
                        </Col>

                        <Col
                            xs={24}
                            md={11}
                        >
                            <Row>
                                <Col
                                    xs={24}
                                    md={24}
                                >
                                    {props.config.is_show_prev_version && (
                                        <div className="prev-link">
                                            <a href={props.config.prev_version_url}>
                                                {props.config.prev_version_name}
                                            </a>
                                        </div>
                                    )}
                                </Col>

                                <Col
                                    md={props.user ? 24 : 12}
                                    className="hide-on-mobile"
                                >
                                    <Flex
                                        vertical
                                        gap={10}
                                        align={props.user ? 'end' : 'stretch'}
                                        style={{
                                            paddingLeft: 5,
                                        }}
                                    >
                                        <ContactCenter
                                            config={{
                                                phone: props.config.short_phone,
                                            }}
                                            icon={<MobilePhoneIcon />}
                                        />
                                        <ContactCenter
                                            config={{
                                                phone: props.config.long_phone,
                                            }}
                                            icon={<PhoneIcon />}
                                        />
                                    </Flex>
                                </Col>

                                {props.descriptor !== 'isnareactcms:auth-page' && !props.user && (
                                    <Col
                                        xs={24}
                                        md={12}
                                    >
                                        <ButtonAuth
                                            style={{ width: '100%', fontSize: md && !lg ? 14 : 16 }}
                                            user={props.user}
                                            url={`/${props.language}${props.config.auth_button_url}`}
                                            config={{ name: props.config.auth_button_text }}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>

                    {props.type === 'portal:site' ? (
                        <div className="service-blocks-grid">
                            <div className="service-blocks">
                                <div id="parts_block-gov-service-list__blocks_0_main_0">
                                    {renderBlockComponents(0)}
                                </div>
                                <div id="parts_block-service-list__blocks_0_main_1">
                                    {renderBlockComponents(1)}
                                </div>
                                <div id="parts_service-link-img__blocks_0_main_2">
                                    {renderBlockComponents(2)}
                                </div>
                                <div id="parts_service-link-img__blocks_0_main_3">
                                    {renderBlockComponents(3)}
                                </div>
                                <div id="parts_service-link-img__blocks_0_main_4">
                                    {renderBlockComponents(4)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        props.descriptor !== 'isnareactcms:auth-page' && (
                            <>
                                <Breadcrumbs
                                    {...props}
                                    translations={translations}
                                />
                                <div className="page-head py-2">
                                    <div className="page-head__title">{props.displayName}</div>
                                </div>
                            </>
                        )
                    )}
                </div>
            </div>
        </>
    );
};

export default (props) => (
    <PartWrapper {...props}>
        <Header {...props} />
    </PartWrapper>
);
